import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CircleStackIcon, Cog6ToothIcon, CpuChipIcon, CreditCardIcon, DocumentTextIcon, KeyIcon, SignalIcon, UsersIcon, WalletIcon, WrenchScrewdriverIcon, } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores';
const quickLinks = [
    {
        to: '/admin/users',
        label: 'Kullanıcılar',
        description: 'Rolleri yönet',
        icon: _jsx(UsersIcon, {}),
    },
    {
        to: '/admin/activity',
        label: 'Etkinlik',
        description: 'SignalR ve günlük',
        icon: _jsx(SignalIcon, {}),
    },
    {
        to: '/admin/storage-providers',
        label: 'Depolama',
        description: 'S3, MinIO, GCS…',
        icon: _jsx(CircleStackIcon, {}),
    },
    {
        to: '/admin/social-providers',
        label: 'Social Login',
        description: 'OAuth sağlayıcıları',
        icon: _jsx(KeyIcon, {}),
    },
    {
        to: '/admin/ai-providers',
        label: 'AI Sağlayıcılar',
        description: 'Model ve API anahtarları',
        icon: _jsx(CpuChipIcon, {}),
    },
    {
        to: '/admin/ai-prompts',
        label: 'AI Promptlar',
        description: 'Sistem promptları',
        icon: _jsx(DocumentTextIcon, {}),
    },
    {
        to: '/admin/payment-providers',
        label: 'Ödeme',
        description: 'Stripe, iyzico…',
        icon: _jsx(CreditCardIcon, {}),
    },
    {
        to: '/admin/crypto-wallets',
        label: 'Kripto',
        description: 'Cüzdan adresleri',
        icon: _jsx(WalletIcon, {}),
    },
    {
        to: '/admin/disclaimers',
        label: 'Disclaimers',
        description: 'Yasal metinler',
        icon: _jsx(DocumentTextIcon, {}),
    },
    {
        to: '/admin/install',
        label: 'Kurulum',
        description: 'Migrasyon ve seed',
        icon: _jsx(WrenchScrewdriverIcon, {}),
    },
    {
        to: '/admin/settings',
        label: 'Ayarlar',
        description: 'Yapılandırma merkezi',
        icon: _jsx(Cog6ToothIcon, {}),
    },
];
async function fetchJson(url, token) {
    try {
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok)
            return null;
        return (await response.json());
    }
    catch {
        return null;
    }
}
function countActive(items) {
    if (!items)
        return { total: 0, active: 0 };
    return {
        total: items.length,
        active: items.filter((i) => i.isActive).length,
    };
}
function formatRelativeTime(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1)
        return 'az önce';
    if (mins < 60)
        return `${mins} dk önce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours} sa önce`;
    const days = Math.floor(hours / 24);
    return `${days} gün önce`;
}
export function Dashboard() {
    const { accessToken } = useAuthStore();
    const [stats, setStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            const [users, signalr, activities, aiProviders, aiPrompts, storage, payment, social, crypto,] = await Promise.all([
                fetchJson('/api/admin/users', accessToken),
                fetchJson('/api/admin/signalr-connections', accessToken),
                fetchJson('/api/admin/activity-logs?take=8', accessToken),
                fetchJson('/api/admin/ai-providers', accessToken),
                fetchJson('/api/admin/ai-prompts', accessToken),
                fetchJson('/api/admin/storage-providers', accessToken),
                fetchJson('/api/admin/payment-providers', accessToken),
                fetchJson('/api/admin/auth-providers', accessToken),
                fetchJson('/api/admin/crypto-wallets', accessToken),
            ]);
            if (cancelled)
                return;
            if (!users) {
                setError('Dashboard verileri yüklenemedi.');
                setLoading(false);
                return;
            }
            const ai = countActive(aiProviders);
            const st = countActive(storage);
            const pay = countActive(payment);
            const soc = countActive(social);
            const cry = countActive(crypto);
            setStats({
                totalUsers: users.length,
                adminCount: users.filter((u) => u.role === 'admin' || u.role === 'system').length,
                editorCount: users.filter((u) => u.role === 'editor').length,
                viewerCount: users.filter((u) => u.role === 'viewer').length,
                liveConnections: signalr?.totalConnections ?? 0,
                liveUsers: signalr?.uniqueUsers ?? 0,
                aiProviders: ai.total,
                aiActive: ai.active,
                storageProviders: st.total,
                storageActive: st.active,
                paymentProviders: pay.total,
                paymentActive: pay.active,
                socialProviders: soc.total,
                socialActive: soc.active,
                cryptoWallets: cry.total,
                cryptoActive: cry.active,
                aiPrompts: aiPrompts?.length ?? 0,
            });
            setRecentActivity(activities ?? []);
            setLoading(false);
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [accessToken]);
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor\u2026" });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsx("header", { className: "admin-page-header", children: _jsxs("div", { children: [_jsx("h1", { children: "Kontrol Paneli" }), _jsx("p", { children: "Sistem \u00F6zeti, canl\u0131 ba\u011Flant\u0131lar ve h\u0131zl\u0131 eri\u015Fim" })] }) }), error ? _jsx("div", { className: "admin-error", children: error }) : null, _jsxs("div", { className: "admin-stats-grid", children: [_jsxs("div", { className: "admin-stat-card", children: [_jsx("div", { className: "admin-stat-icon", "aria-hidden": true, children: _jsx(UsersIcon, {}) }), _jsxs("div", { className: "admin-stat-content", children: [_jsx("div", { className: "admin-stat-value", children: stats?.totalUsers ?? 0 }), _jsx("div", { className: "admin-stat-label", children: "Toplam kullan\u0131c\u0131" })] })] }), _jsxs("div", { className: "admin-stat-card", children: [_jsx("div", { className: "admin-stat-icon", "aria-hidden": true, children: _jsx(SignalIcon, {}) }), _jsxs("div", { className: "admin-stat-content", children: [_jsx("div", { className: "admin-stat-value", children: stats?.liveConnections ?? 0 }), _jsxs("div", { className: "admin-stat-label", children: ["Canl\u0131 ba\u011Flant\u0131 \u00B7 ", stats?.liveUsers ?? 0, " kullan\u0131c\u0131"] })] })] }), _jsxs("div", { className: "admin-stat-card", children: [_jsx("div", { className: "admin-stat-icon", "aria-hidden": true, children: _jsx(CpuChipIcon, {}) }), _jsxs("div", { className: "admin-stat-content", children: [_jsxs("div", { className: "admin-stat-value", children: [stats?.aiActive ?? 0, _jsxs("span", { className: "admin-stat-suffix", children: ["/", stats?.aiProviders ?? 0] })] }), _jsx("div", { className: "admin-stat-label", children: "Aktif AI sa\u011Flay\u0131c\u0131" })] })] }), _jsxs("div", { className: "admin-stat-card", children: [_jsx("div", { className: "admin-stat-icon", "aria-hidden": true, children: _jsx(CircleStackIcon, {}) }), _jsxs("div", { className: "admin-stat-content", children: [_jsxs("div", { className: "admin-stat-value", children: [stats?.storageActive ?? 0, _jsxs("span", { className: "admin-stat-suffix", children: ["/", stats?.storageProviders ?? 0] })] }), _jsx("div", { className: "admin-stat-label", children: "Aktif depolama" })] })] })] }), _jsxs("div", { className: "admin-dashboard-grid", children: [_jsxs("section", { className: "admin-card admin-dashboard-panel", children: [_jsx("div", { className: "admin-card-header", children: _jsx("h2", { children: "Rol da\u011F\u0131l\u0131m\u0131" }) }), _jsxs("div", { className: "admin-card-body", children: [_jsxs("div", { className: "admin-role-breakdown", children: [_jsxs("div", { className: "admin-role-row", children: [_jsx("span", { children: "Admin / System" }), _jsx("strong", { children: stats?.adminCount ?? 0 })] }), _jsxs("div", { className: "admin-role-row", children: [_jsx("span", { children: "Editor" }), _jsx("strong", { children: stats?.editorCount ?? 0 })] }), _jsxs("div", { className: "admin-role-row", children: [_jsx("span", { children: "Viewer" }), _jsx("strong", { children: stats?.viewerCount ?? 0 })] })] }), _jsxs("div", { className: "admin-provider-summary", children: [_jsxs("div", { className: "admin-provider-chip", children: [_jsx("span", { children: "\u00D6deme" }), _jsxs("strong", { children: [stats?.paymentActive ?? 0, "/", stats?.paymentProviders ?? 0] })] }), _jsxs("div", { className: "admin-provider-chip", children: [_jsx("span", { children: "Social" }), _jsxs("strong", { children: [stats?.socialActive ?? 0, "/", stats?.socialProviders ?? 0] })] }), _jsxs("div", { className: "admin-provider-chip", children: [_jsx("span", { children: "Kripto" }), _jsxs("strong", { children: [stats?.cryptoActive ?? 0, "/", stats?.cryptoWallets ?? 0] })] }), _jsxs("div", { className: "admin-provider-chip", children: [_jsx("span", { children: "Prompt" }), _jsx("strong", { children: stats?.aiPrompts ?? 0 })] })] })] })] }), _jsxs("section", { className: "admin-card admin-dashboard-panel", children: [_jsxs("div", { className: "admin-card-header admin-card-header-row", children: [_jsx("h2", { children: "Son etkinlikler" }), _jsx(Link, { to: "/admin/activity", className: "admin-text-link", children: "T\u00FCm\u00FC" })] }), _jsx("div", { className: "admin-card-body admin-card-body-flush", children: recentActivity.length === 0 ? (_jsx("div", { className: "admin-empty-state admin-empty-state-compact", children: _jsx("p", { children: "Hen\u00FCz kay\u0131tl\u0131 etkinlik yok." }) })) : (_jsx("ul", { className: "admin-activity-feed", children: recentActivity.map((item) => (_jsxs("li", { className: "admin-activity-feed-item", children: [_jsxs("div", { className: "admin-activity-feed-main", children: [_jsx("span", { className: "admin-activity-feed-action", children: item.action }), _jsxs("span", { className: "admin-activity-feed-meta", children: [item.user, item.details ? ` · ${item.details}` : ''] })] }), _jsx("time", { className: "admin-activity-feed-time", dateTime: item.timestamp, children: formatRelativeTime(item.timestamp) })] }, item.id))) })) })] })] }), _jsxs("div", { className: "admin-quick-actions", children: [_jsx("h2", { children: "H\u0131zl\u0131 eri\u015Fim" }), _jsx("div", { className: "admin-actions-grid", children: quickLinks.map((link) => (_jsxs(Link, { to: link.to, className: "admin-action-card", children: [_jsx("span", { className: "admin-action-icon", "aria-hidden": true, children: link.icon }), _jsx("span", { className: "admin-action-label", children: link.label }), _jsx("span", { className: "admin-action-desc", children: link.description })] }, link.to))) })] })] }));
}
